/**
 * Email validation utilities for reuse across components
 */

/**
 * Validates multiple email addresses from a string
 * @param {string} emailString - Email string (comma or semicolon separated)
 * @param {boolean} required - Whether email is required
 * @returns {object} Validation result
 */
export const validateEmailAddresses = (emailString, required = false) => {
    if (!emailString || emailString.trim() === '') {
        return { 
            isValid: !required,
            emails: [], 
            invalidEmails: [],
            errors: required ? ['Email is required'] : []
        };
    }

    // Split by comma or semicolon and trim whitespace
    const emailList = emailString
        .split(/[,;]/)
        .map(email => email.trim())
        .filter(email => email.length > 0);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = [];
    const invalidEmails = [];

    emailList.forEach(email => {
        if (emailRegex.test(email)) {
            validEmails.push(email);
        } else {
            invalidEmails.push(email);
        }
    });

    return {
        isValid: invalidEmails.length === 0 && (!required || validEmails.length > 0),
        emails: validEmails,
        invalidEmails: invalidEmails,
        errors: invalidEmails.length > 0 ? [`Invalid email(s): ${invalidEmails.join(', ')}`] : []
    };
};

/**
 * Formats email list as comma-separated string
 * @param {array} emails - Array of email addresses
 * @returns {string} Comma-separated email string
 */
export const formatEmailsAsString = (emails) => {
    if (!Array.isArray(emails)) return '';
    return emails.filter(email => email && email.trim()).join(', ');
};

/**
 * Parses email string into array
 * @param {string} emailString - Email string (comma or semicolon separated)
 * @returns {array} Array of email addresses
 */
export const parseEmailsFromString = (emailString) => {
    if (!emailString || emailString.trim() === '') return [];
    
    return emailString
        .split(/[,;]/)
        .map(email => email.trim())
        .filter(email => email.length > 0);
};

/**
 * Validates form data containing multiple email fields
 * @param {object} formData - Form data object
 * @param {array} emailFields - Array of email field configurations
 * @returns {object} Validation result
 */
export const validateFormEmails = (formData, emailFields) => {
    const errors = {};
    let hasErrors = false;

    emailFields.forEach(({ fieldPath, required = false, label = 'Email' }) => {
        // Support nested field paths like 'email_groups.0.emails'
        const value = getNestedValue(formData, fieldPath);
        const validation = validateEmailAddresses(value, required);
        
        if (!validation.isValid) {
            errors[fieldPath] = validation.errors;
            hasErrors = true;
        }
    });

    return {
        isValid: !hasErrors,
        errors: errors
    };
};

/**
 * Helper function to get nested object values
 * @param {object} obj - Object to search
 * @param {string} path - Dot notation path (e.g., 'email_groups.0.emails')
 * @returns {any} Value at path
 */
const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
};